import get from 'lodash-es/get';
import { Component, ElementType, FC, ReactNode } from 'react';
import { connect } from 'react-redux';
import Text, { TextPresets } from '@stripcash/ui-kit/components/Text';

import SimpleLayout from 'common/layouts/SimpleLayout';
import ErrorReporter from 'common/helpers/ErrorReporter';
import ErrorNavigation from 'common/components/ErrorNavigation';
import { isUserLoggedInSelector } from 'users/selectors';

import styles from './styles.module.css';

type ErrorBoundaryPropTypes = {
  isLoggedIn: boolean;
};

const ErrorBoundaryInfo: FC<ErrorBoundaryPropTypes> = ({ isLoggedIn }) => (
  <>
    <Text preset={TextPresets.H1}>
      We&apos;re sorry - something went wrong :(
    </Text>
    <Text preset={TextPresets.H3}>Refresh the page or try again later</Text>
    <ErrorNavigation isLoggedIn={isLoggedIn} />
  </>
);

const ErrorBoundaryContent: FC<ErrorBoundaryPropTypes> = ({ isLoggedIn }) => (
  <div className={styles.errorBoundaryWrapper}>
    <ErrorBoundaryInfo isLoggedIn={isLoggedIn} />
  </div>
);

const ErrorBoundaryRootContent: FC<ErrorBoundaryPropTypes> = ({
  isLoggedIn,
}) => (
  <div className={styles.errorBoundaryRootWrapper}>
    <SimpleLayout isColumns>
      <ErrorBoundaryInfo isLoggedIn={isLoggedIn} />
    </SimpleLayout>
  </div>
);

class ErrorBoundaryInner extends Component<{
  content: ElementType;
  children: ReactNode;
  isLoggedIn: boolean;
  locationKey: string;
}> {
  static defaultProps = {
    children: null,
    content: ErrorBoundaryContent,
  };

  state = {
    hasError: false,
    prevLocationKey: '',
  };

  /**
   * @inheritDoc
   */
  static getDerivedStateFromProps(props, state) {
    if (props.locationKey !== state.prevLocationKey) {
      return {
        prevLocationKey: props.locationKey,
        hasError: false,
      };
    }

    return null;
  }

  /**
   * @inheritDoc
   */
  componentDidCatch(error, info) {
    this.setState({ hasError: true });

    ErrorReporter.captureException(error, { info, errorType: 'ErrorBoundary' });
  }

  /**
   * @inheritDoc
   */
  render() {
    const { content: ErrorContent, children, isLoggedIn } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return <ErrorContent isLoggedIn={isLoggedIn} />;
    }

    return children;
  }
}

const ErrorBoundaryWrapper = (props) => {
  const { children, locationKey, isLoggedIn, content } = props;

  return (
    <ErrorBoundaryInner
      content={content}
      isLoggedIn={isLoggedIn}
      locationKey={locationKey}
    >
      {children}
    </ErrorBoundaryInner>
  );
};

const mapStateToProps = (state) => {
  const locationKey = get(state, 'router.location.pathname');
  const isLoggedIn = isUserLoggedInSelector(state);

  return {
    isLoggedIn,
    locationKey,
  };
};

export const ErrorBoundary = connect(mapStateToProps)(ErrorBoundaryWrapper);

export const ErrorBoundaryRoot = (props) => {
  const { children } = props;

  return (
    <ErrorBoundary content={ErrorBoundaryRootContent}>{children}</ErrorBoundary>
  );
};
